// Turns a stretch of the user's log entries into the number series the weekly
// check-up screen draws: the weigh-in / skinfold / waist trend charts, the
// progress-and-setback bars for last week's training, and the per-weekday
// calorie bars.
//
// Everything here is a plain function: it takes the data it needs as arguments
// and returns its answer. It reads no database, touches no Angular service and
// never looks at the document, so a unit test can drive it with made-up objects.
// The page keeps the parts that genuinely need those things — the database
// reads, resolving a workout's effective exercises, reading the chart colours
// out of the theme, and assigning the results to its own fields.
//
// Split out of weekly-checkup-page.ts for deferred finding df-93788345eac9.

import { DateTime } from 'luxon';
import { getEntryLocalTimeInDesiredTimeZone, Log_Entry, LogRecordType } from '@cf-shared/data-model/user-log';
import { getAverageCalipersSum, getAverageWaist, hasAnyCaliperReadings, hasAnyTapeReadings, hasMeasuredBodyFat, Log_WeighIn } from '@cf-shared/data-model/log-weigh-in';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { Log_WorkoutExercise } from '@cf-shared/data-model/log-workout';
import { Gender, SetSuccess, SetType } from '@cf-shared/enums';
import { INFOODS } from '@cf-shared/data-model/food';
import { roundToPrecision } from '@cf-shared/shared-util';
import type { TranslatedString } from 'models/translated-string';

/** one bar on the "what did you eat each day last week" row */
export interface WeekdayLogData {
  dateISO: string; // ISO date string
  weekdayLabel: TranslatedString;
  consumedCalories: number;
  targetCalories: number;
  checked?: boolean; // For checkbox state
}

/** the three body-measurement series, one value per day, `undefined` where nothing was measured */
export interface WeighInTrendSeries {
  /** the ISO dates the values line up with, oldest first */
  dates: string[];
  weights: number[];
  calipers: number[];
  waists: number[];
}

/** the measurement units the most recent entries used, so the caller can pick a conversion */
export interface LastMeasurementUnits {
  weightUnit: string | undefined;
  tapeUnit: string | undefined;
}

/** one bar pair on the training-progress chart, plus the dates they line up with */
export interface WorkoutProgressSeries {
  /** the ISO dates of the days that had at least one concluded set, oldest first */
  dates: string[];
  /** how many exercises went forward that day, one entry per date */
  progressions: number[];
  /** how many went backward that day, as NEGATIVE numbers so the chart draws them downward */
  regressions: number[];
}

/**
 * Index the entries of one type by the local calendar day they belong to.
 *
 * Later entries win, matching the order the database hands them back, so a day
 * with several entries of the same type keeps the last one.
 */
export function mapEntriesByDayAndType(logEntries: Log_Entry[], type: LogRecordType, now: DateTime): { [date: string]: Log_Entry } {
  let array = logEntries.filter(e => e.type === type);
  let map = {};
  for (let e of array) {
    let d = getEntryLocalTimeInDesiredTimeZone(e, now).toISODate();
    map[d] = e;
  }
  return map;
}

/** every ISO date from `from` to `until` inclusive, oldest first */
export function listDaysInRange(from: DateTime, until: DateTime): string[] {
  const days: string[] = [];
  for (let d = from; d <= until; d = d.plus({ days: 1 })) {
    days.push(d.toISODate());
  }
  return days;
}

/**
 * Which measurement units the user's most recent entries were recorded in.
 *
 * Asked separately from building the series because the caller needs these to
 * work out the unit conversion, and the conversion factor is then an argument to
 * {@link buildWeighInTrendSeries}. Only entries carrying a usable value count, so
 * a blank weigh-in cannot reset the unit.
 */
export function findLastMeasurementUnits(logEntries: Log_Entry[], days: string[], now: DateTime): LastMeasurementUnits {
  const weighInsByDay = mapEntriesByDayAndType(logEntries, LogRecordType.WeighIn, now);
  let weightUnit: string = undefined;
  let tapeUnit: string = undefined;

  for (let day of days) {
    const weightIn = weighInsByDay[day] as Log_WeighIn;
    if (weightIn && weightIn.weight_unit && weightIn.weight_kg > 0) {
      weightUnit = weightIn.weight_unit;
    }
    if (weightIn && weightIn.tapeMeasures && weightIn.tapeMeasures.unit && weightIn.tapeMeasures.waist_cm && weightIn.tapeMeasures.waist_cm[0] > 0) {
      tapeUnit = weightIn.tapeMeasures.unit;
    }
  }

  return { weightUnit, tapeUnit };
}

/**
 * The weight, skinfold and waist series for the trend charts, one slot per day
 * in `days` whether or not anything was measured that day.
 *
 * A day with no measurement gets `undefined` rather than being left out, because
 * the chart draws a gap there and the three series have to stay lined up with
 * `dates`.
 */
export function buildWeighInTrendSeries(params: {
  logEntries: Log_Entry[];
  days: string[];
  gender: Gender;
  weightUnitFactor: number;
  tapeUnitFactor: number;
  now: DateTime;
}): WeighInTrendSeries {
  const { logEntries, days, gender, weightUnitFactor, tapeUnitFactor, now } = params;
  const weighInsByDay = mapEntriesByDayAndType(logEntries, LogRecordType.WeighIn, now);

  const weights: number[] = [];
  const calipers: number[] = [];
  const waists: number[] = [];

  for (let day of days) {
    const weightIn = weighInsByDay[day] as Log_WeighIn;

    // weight measurement
    if (weightIn && weightIn.weight_kg && weightIn.weight_kg > 0) {
      weights.push(roundToPrecision(weightIn.weight_kg * weightUnitFactor, 0.1));
    } else {
      weights.push(undefined);
    }

    // caliper measurement
    const calipersSum = weightIn && getAverageCalipersSum(weightIn, gender);
    if (calipersSum && calipersSum > 0) {
      calipers.push(roundToPrecision(calipersSum, 0.1));
    } else {
      calipers.push(undefined);
    }

    // waist measurement
    if (weightIn && weightIn.tapeMeasures && weightIn.tapeMeasures.waist_cm && weightIn.tapeMeasures.waist_cm.length > 0) {
      const waistAvg = getAverageWaist(weightIn);
      if (waistAvg && waistAvg > 0) {
        waists.push(roundToPrecision(waistAvg * tapeUnitFactor, 0.1));
      } else {
        waists.push(undefined);
      }
    } else {
      waists.push(undefined);
    }
  }

  return { dates: [...days], weights, calipers, waists };
}

/**
 * Count, per training day, how many exercises went forward and how many went
 * backward.
 *
 * This is the rule that decides what the check-up screen calls progress and what
 * it calls a setback, and it is why this module exists: inside the page it could
 * only be exercised by opening the screen with a real week of workouts in the
 * database.
 *
 * Each exercise is judged by its FIRST set that settles the question, and then
 * the exercise is done — later sets of the same exercise are not looked at. A set
 * settles it by meeting what the plan asked for at the moment it was logged
 * (`planPathBackup`, so a set is measured against its own prescription rather
 * than against a target that has climbed since), or by being a first working set
 * the user failed. A set with no backed-up plan path, or with no recorded
 * numbers, settles nothing and the search moves on.
 *
 * A day where nothing was concluded at all is left out entirely rather than
 * drawn as a zero, because a rest day and a wasted session should not look the
 * same on the chart.
 *
 * `daysWithExercises` must be ordered oldest first; the caller resolves each
 * day's effective exercises, which needs the plan and so cannot happen here.
 */
export function countWorkoutProgressAndSetbacks(
  daysWithExercises: { day: string; exercises: Log_WorkoutExercise[] }[],
): WorkoutProgressSeries {
  const dates: string[] = [];
  const progressions: number[] = [];
  const regressions: number[] = [];

  for (const { day, exercises } of daysWithExercises) {
    let progressionCount = 0;
    let regressionCount = 0;
    let someExerciseSetDone = false;

    for (let exercise of exercises) {
      for (let iSet = 0; iSet < exercise.sets.length; iSet++) {
        const set = exercise.sets[iSet];
        const planPath = set.planPathBackup;
        if (!planPath) {
          continue;
        }
        const minRep = planPath.min_reps;
        const targetWeight = planPath.target_weight_kg;
        const setStats = set.stats;
        if (!setStats) {
          continue;
        }
        someExerciseSetDone = true;

        if (minRep !== undefined && setStats.reps >= minRep && setStats.weight_kg >= targetWeight ||
          minRep === undefined && setStats.weight_kg >= targetWeight
        ) {
          progressionCount++;
          break;
        } else if (set.setType === SetType.Regular1st && set.setSuccess === SetSuccess.Fail) {
          regressionCount--;
          break;
        }
      }
    }

    if (someExerciseSetDone) {
      progressions.push(progressionCount);
      regressions.push(regressionCount);
      dates.push(day);
    }
  }

  return { dates, progressions, regressions };
}

/**
 * One bar per day the user logged their food, plus the tallest number on the
 * row so the caller can scale the bars.
 *
 * Days with no day-start entry are skipped rather than drawn empty. `maxCalories`
 * is 0 when there are no such days at all, which is what an empty row scales to.
 */
export function buildPreviousWeekdayLogs(params: {
  logEntries: Log_Entry[];
  days: string[];
  weekdayInitials: TranslatedString[];
  now: DateTime;
}): { days: WeekdayLogData[]; maxCalories: number } {
  const { logEntries, days, weekdayInitials, now } = params;
  const logsByDay = mapEntriesByDayAndType(logEntries, LogRecordType.DayStart, now);

  const result: WeekdayLogData[] = [];
  for (const dayKey of days) {
    const logEntry = logsByDay[dayKey] as Log_DayStart;
    if (!logEntry) continue;
    const localTime = getEntryLocalTimeInDesiredTimeZone(logEntry, now);
    result.push({
      dateISO: dayKey,
      weekdayLabel: weekdayInitials[localTime.weekday - 1],
      consumedCalories: Math.round(logEntry.nutrients[INFOODS.Calories]) | 0,
      targetCalories: Math.round(logEntry.targets[INFOODS.Calories].target),
      checked: true,
    });
  }

  // calculate max target calories for scaling. Math.max of nothing is -Infinity,
  // which would scale every bar wrongly, so an empty row answers 0.
  const maxCalories = result.length === 0
    ? 0
    : Math.max(...result.map(day => Math.max(day.targetCalories, day.consumedCalories)));

  return { days: result, maxCalories };
}

/**
 * The most recent weigh-in that carries a body-composition measurement — a waist
 * tape or a suprailiac skinfold — or `undefined` if none of them does.
 *
 * The screen asks this to decide which of the two body-composition charts to
 * draw, so "the newest one that actually measured something" is the question,
 * not "the newest one".
 */
export function pickLatestBodyfatLog(logEntries: Log_Entry[]): Log_WeighIn | undefined {
  const weighIns = logEntries.filter(e => e.type === LogRecordType.WeighIn) as Log_WeighIn[];

  const reversedWeighIns = [...weighIns].reverse();
  return reversedWeighIns.find(w => hasMeasuredBodyFat(w));
}

/** does this weigh-in carry a waist tape measurement, so the waist chart has something to draw? */
export function hasWaistMeasurement(weighIn: Log_WeighIn | undefined): boolean {
  return hasAnyTapeReadings(weighIn);
}

/** does this weigh-in carry a skinfold measurement, so the skinfold chart has something to draw? */
export function hasSkinfoldMeasurement(weighIn: Log_WeighIn | undefined): boolean {
  return hasAnyCaliperReadings(weighIn);
}
